// Batch Details progress bar — redesigned per "Batch Progress Bar for Batch
// Details Page" (BCM-898, Aug 2026).
//
// The timeline now shows each status with a visible LABEL (alternating above /
// below the line), squares for the Current and Next status and circles for the
// Completed / Pending ones, a green connector up to the current status and a grey
// one after it, a Status Legend, and a check on the final status once the batch
// is complete.
//
// An optional status header (Current Status · Next Status · Pending Action ·
// Assigned To · Batch Resets + action buttons) renders above the timeline when
// `showStatusHeader` is passed — this is the Goods Receipt reuse (BCM-898). The
// header, the Assigned To value, the reset modal and the action buttons are all
// driven by additive props, so the existing Batch Execution consumers that pass
// only `progressBar` are unchanged.
import React, { useState, useRef, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import moment from 'moment';

const COLORS = {
  completed: '#408E0F',
  current: '#0A1E5E', // navy square
  upcoming: '#F26722', // orange square
  future: '#C4C4C4', // grey circle
  connectorDone: '#408E0F',
  connectorPending: '#D9D9D9',
  label: '#374151',
};

const styles = () => ({
  root: {
    width: '100%',
  },
  // ── Status header (Goods Receipt reuse) ─────────────────────────────────
  header: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '24px 32px',
    marginBottom: '12px',
  },
  headerField: {
    minWidth: 'auto',
  },
  headerLabel: {
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: '#111827',
    marginBottom: '4px',
    fontFamily: 'Roboto, sans-serif',
  },
  headerValueCurrent: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: COLORS.current,
    fontFamily: 'Roboto, sans-serif',
  },
  headerValueNext: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: COLORS.upcoming,
    fontFamily: 'Roboto, sans-serif',
  },
  headerValue: {
    fontSize: '0.875rem',
    color: '#111827',
    fontFamily: 'Roboto, sans-serif',
  },
  pendingList: {
    margin: 0,
    paddingLeft: '18px',
    maxWidth: '320px',
    fontSize: '0.875rem',
    lineHeight: 1.35,
    color: '#111827',
    fontFamily: 'Roboto, sans-serif',
  },
  pendingSingle: {
    maxWidth: '320px',
    fontSize: '0.875rem',
    lineHeight: 1.35,
    color: '#111827',
    fontFamily: 'Roboto, sans-serif',
  },
  resetLink: {
    fontSize: '0.875rem',
    color: COLORS.current,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'Roboto, sans-serif',
  },
  resetPlain: {
    fontSize: '0.875rem',
    color: '#111827',
    fontFamily: 'Roboto, sans-serif',
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  // ── Timeline card ───────────────────────────────────────────────────────
  card: {
    border: '1px solid #E3E3E3',
    borderRadius: '6px',
    backgroundColor: '#FCFCFC',
    padding: '8px 10px 10px',
  },
  // The legend gets its own row above the scroller so it can never overlap the
  // top labels and does not drift when the timeline is scrolled sideways.
  legend: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '2px',
  },
  scroller: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '4px',
    '&::-webkit-scrollbar': { height: '6px' },
    '&::-webkit-scrollbar-track': { backgroundColor: '#EFEFEF', borderRadius: '3px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#BDBDBD', borderRadius: '3px' },
  },
  legendBox: {
    minWidth: 150,
  },
  legendTitle: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    textDecoration: 'underline',
    color: '#111827',
    marginBottom: '4px',
    textAlign: 'center',
    fontFamily: 'Roboto, sans-serif',
  },
  legendGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    gap: '4px 14px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.6875rem',
    color: '#374151',
    fontFamily: 'Roboto, sans-serif',
  },
  legendSwatchCircle: { width: 11, height: 11, borderRadius: '50%', flexShrink: 0 },
  legendSwatchSquare: { width: 11, height: 11, borderRadius: '2px', flexShrink: 0 },
  track: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 'fit-content',
    // Clearance for the absolutely placed labels + their leader ticks.
    padding: '66px 56px',
    boxSizing: 'border-box',
  },
  node: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 14,
    height: 14,
  },
  label: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 104,
    fontSize: '0.6875rem',
    lineHeight: 1.3,
    textAlign: 'center',
    color: COLORS.label,
    fontFamily: 'Roboto, sans-serif',
    overflowWrap: 'break-word',
  },
  labelAbove: {
    bottom: 'calc(100% + 14px)',
  },
  labelBelow: {
    top: 'calc(100% + 14px)',
  },
  // Leader tick: the short line that joins a dot to its status name, so each
  // label visibly belongs to its own node.
  leader: {
    position: 'absolute',
    left: '50%',
    width: 1,
    height: 11,
    transform: 'translateX(-50%)',
  },
  leaderAbove: {
    bottom: '100%',
  },
  leaderBelow: {
    top: '100%',
  },
  labelCompleted: { color: COLORS.completed },
  labelCurrent: { color: COLORS.current, fontWeight: 700 },
  labelUpcoming: { color: COLORS.upcoming },
  labelFuture: { color: '#9AA0A6' },
  circle: { width: 14, height: 14, borderRadius: '50%', flexShrink: 0 },
  square: { width: 14, height: 14, borderRadius: '2px', flexShrink: 0 },
  circleCompleted: { backgroundColor: COLORS.completed },
  squareCurrent: { backgroundColor: COLORS.current },
  squareUpcoming: { backgroundColor: COLORS.upcoming },
  circleFuture: { backgroundColor: COLORS.future },
  checkNode: {
    width: 15,
    height: 15,
    borderRadius: '50%',
    backgroundColor: COLORS.completed,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIcon: { color: '#FFFFFF', fontSize: 11 },
  connector: {
    flex: '1 1 auto',
    // Labels alternate above/below, so same-row neighbours sit two nodes
    // apart (node + connector + node + connector). That gap must clear the
    // 104px label width or the text runs into its same-row neighbour's —
    // this is what overlapped once a batch had enough replayed reset blocks
    // to pack nodes tightly. minWidth: 50 keeps that gap wide enough at any
    // node count; the scroller (below) absorbs the resulting width.
    minWidth: 50,
    height: 2,
    alignSelf: 'center',
  },
  // ── Reset history modal ─────────────────────────────────────────────────
  modalPaper: {
    padding: '24px 32px 32px',
    minWidth: 620,
    maxWidth: 780,
  },
  modalHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: '8px',
  },
  modalCancel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 700,
    fontFamily: 'Roboto, sans-serif',
  },
  modalTitle: {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '1.5rem',
    margin: '4px 0 20px',
    fontFamily: 'Roboto, sans-serif',
  },
  resetTable: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #D9D9D9',
  },
  resetTh: {
    textAlign: 'left',
    padding: '14px 18px',
    fontWeight: 700,
    fontSize: '0.9375rem',
    backgroundColor: '#F7F7F7',
    borderBottom: '1px solid #D9D9D9',
    fontFamily: 'Roboto, sans-serif',
  },
  resetTd: {
    padding: '14px 18px',
    fontSize: '0.9375rem',
    borderBottom: '1px solid #E5E5E5',
    fontFamily: 'Roboto, sans-serif',
  },
});

// Derive the ordered sequence + the current / next indices once.
function analyse(statusSequence) {
  const sorted = [...statusSequence].sort(
    (a, b) => (a.sequence || 0) - (b.sequence || 0)
  );
  const currentIndex = sorted.findIndex((s) => s.state === 'current');
  let upcomingIndex = sorted.findIndex((s) => s.state === 'upcoming');
  if (upcomingIndex === -1 && currentIndex !== -1) {
    upcomingIndex = currentIndex + 1;
  }
  // Header-only fallback: some flows send no 'current' node at all (a batch
  // whose whole sequence is already completed, for instance). The header then
  // reads the last completed status instead of showing '-', and its Next value
  // the first not-yet-completed one. The dots keep using currentIndex /
  // upcomingIndex, so the timeline itself is unchanged.
  let headerIndex = currentIndex;
  if (headerIndex === -1) {
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      if (sorted[i].state === 'completed') {
        headerIndex = i;
        break;
      }
    }
  }
  const headerUpcomingIndex =
    upcomingIndex !== -1 ? upcomingIndex : headerIndex + 1;
  return { sorted, currentIndex, upcomingIndex, headerIndex, headerUpcomingIndex };
}

function BatchProgressBar({
  progressBar,
  classes,
  showStatusHeader = false,
  assignedTo,
  batchNumber,
  headerActions,
  // BCM-898: the reset-history modal is driven by `batchResetDetails` from
  // fetchDeliveryScheduleBatchDetails (same object level as bomComponentDetails):
  //   [{ batchResetTimestamp, batchResetBy, batchResetFor }]
  // Falls back to progressBar.batchResets / resetCount when not supplied so the
  // Batch Execution consumers (which pass only `progressBar`) are unchanged.
  batchResetDetails,
}) {
  const [resetsOpen, setResetsOpen] = useState(false);
  const scrollerRef = useRef(null);
  const currentNodeRef = useRef(null);

  // Each batch reset appends another replay block, so the sequence can run
  // well past what fits the popup width. The scroller (below) opens at its
  // leftmost edge by default, which would bury the batch's current status
  // under however much reset history precedes it — bring the current node
  // into view instead so the latest status is always what's shown first.
  useEffect(() => {
    const container = scrollerRef.current;
    const node = currentNodeRef.current;
    if (!container || !node) return;
    const target =
      node.offsetLeft - container.clientWidth / 2 + node.offsetWidth / 2;
    container.scrollLeft = Math.max(0, target);
  }, [progressBar]);

  if (!progressBar || !progressBar.statusSequence) {
    return null;
  }

  const { statusSequence, pendingRequirements, resetCount } =
    progressBar;
  const { sorted, currentIndex, upcomingIndex, headerIndex, headerUpcomingIndex } =
    analyse(statusSequence);
  const latestKnownIndex = currentIndex !== -1 ? currentIndex : headerIndex;

  const effectiveState = (index) =>
    index === upcomingIndex ? 'upcoming' : sorted[index].state;

  const currentStatusText =
    headerIndex !== -1 ? sorted[headerIndex].statusName : '-';
  const nextStatusText =
    headerUpcomingIndex > 0 && headerUpcomingIndex < sorted.length
      ? sorted[headerUpcomingIndex].statusName
      : '-';

  // Pending Action lists only the requirements that are NOT yet fulfilled —
  // a fulfilled requirement is history, not an action (BCM-898 review). Guard
  // on a strict fulfilled-is-true test so a missing flag still counts as pending.
  const pendingActions = (pendingRequirements || [])
    .map((r) => r.label)
    .filter((l) => l !== null && l !== undefined && String(l).trim() !== '');

  // ── the timeline node/connector renderers ──────────────────────────────
  const renderDot = (state, isLast) => {
    if (state === 'completed' && isLast) {
      return (
        <div className={classes.checkNode}>
          <CheckIcon className={classes.checkIcon} />
        </div>
      );
    }
    switch (state) {
      case 'completed':
        return <div className={`${classes.circle} ${classes.circleCompleted}`} />;
      case 'current':
        return <div className={`${classes.square} ${classes.squareCurrent}`} />;
      case 'upcoming':
        return <div className={`${classes.square} ${classes.squareUpcoming}`} />;
      default:
        return <div className={`${classes.circle} ${classes.circleFuture}`} />;
    }
  };

  const stateColor = (state) => {
    switch (state) {
      case 'completed':
        return COLORS.completed;
      case 'current':
        return COLORS.current;
      case 'upcoming':
        return COLORS.upcoming;
      default:
        return COLORS.future;
    }
  };

  const labelClass = (state) => {
    switch (state) {
      case 'completed':
        return `${classes.label} ${classes.labelCompleted}`;
      case 'current':
        return `${classes.label} ${classes.labelCurrent}`;
      case 'upcoming':
        return `${classes.label} ${classes.labelUpcoming}`;
      default:
        return `${classes.label} ${classes.labelFuture}`;
    }
  };

  const timeline = (
    <div className={classes.card}>
      {/* Status Legend — its own row above the scroller, so it can never sit on
          top of a label and never drifts while the track is scrolled. */}
      <div className={classes.legend}>
        <div className={classes.legendBox}>
          <div className={classes.legendTitle}>Status Legend</div>
          <div className={classes.legendGrid}>
            <span className={classes.legendItem}>
              <span
                className={classes.legendSwatchSquare}
                style={{ backgroundColor: COLORS.current }}
              />
              Current
            </span>
            <span className={classes.legendItem}>
              <span
                className={classes.legendSwatchSquare}
                style={{ backgroundColor: COLORS.upcoming }}
              />
              Next
            </span>
            <span className={classes.legendItem}>
              <span
                className={classes.legendSwatchCircle}
                style={{ backgroundColor: COLORS.completed }}
              />
              Completed
            </span>
            <span className={classes.legendItem}>
              <span
                className={classes.legendSwatchCircle}
                style={{ backgroundColor: COLORS.future }}
              />
              Pending
            </span>
          </div>
        </div>
      </div>

      <div className={classes.scroller} ref={scrollerRef}>
        <div className={classes.track}>
          {sorted.map((status, index) => {
            const state = effectiveState(index);
            const isLast = index === sorted.length - 1;
            const label = status.statusName || status.statusDisplayName || '';
            // Alternate labels above (odd) / below (even) the line.
            const labelAbove = index % 2 === 1;
            // Connector before this node is green when the node to its left is
            // completed (i.e. everything up to and including the current node).
            const connectorDone =
              index > 0 && effectiveState(index - 1) === 'completed';

            return (
              <React.Fragment key={`${status.statusName || 'n'}-${index}`}>
                {index > 0 && (
                  <div
                    className={classes.connector}
                    style={{
                      backgroundColor: connectorDone
                        ? COLORS.connectorDone
                        : COLORS.connectorPending,
                    }}
                  />
                )}
                <Tooltip title={<span>{label}</span>} placement="top" arrow>
                  <div
                    className={classes.node}
                    ref={index === latestKnownIndex ? currentNodeRef : undefined}
                  >
                    <span
                      className={`${classes.leader} ${
                        labelAbove ? classes.leaderAbove : classes.leaderBelow
                      }`}
                      style={{ backgroundColor: stateColor(state) }}
                    />
                    <span
                      className={`${labelClass(state)} ${
                        labelAbove ? classes.labelAbove : classes.labelBelow
                      }`}
                    >
                      {label}
                    </span>
                    {renderDot(state, isLast)}
                  </div>
                </Tooltip>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (!showStatusHeader) {
    return <div className={classes.root}>{timeline}</div>;
  }

  // ── Status header (Goods Receipt) ───────────────────────────────────────
  // Prefer the new batchResetDetails feed; fall back to the legacy batchResets /
  // resetCount. Normalise every source to { resetBy, resetFor, timestamp }.
  const hasResetDetails =
    Array.isArray(batchResetDetails) && batchResetDetails.length > 0;
  const resetRows = hasResetDetails
    ? batchResetDetails.map((r) => ({
        resetBy: r.batchResetBy,
        resetFor: r.batchResetFor,
        timestamp: r.batchResetTimeStamp,
      }))
    : Array.from({ length: Number(resetCount) || 0 }, () => ({
        resetBy: null,
        resetFor: null,
        timestamp: null,
      }));
  const resetNumber = hasResetDetails
    ? resetRows.length
    : Number(resetCount) || resetRows.length || 0;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerField}>
          <div className={classes.headerLabel}>Current Status</div>
          <div className={classes.headerValueCurrent}>{currentStatusText}</div>
        </div>
        <div className={classes.headerField}>
          <div className={classes.headerLabel}>Next Status</div>
          <div className={classes.headerValueNext}>{nextStatusText}</div>
        </div>
        <div className={classes.headerField}>
          <div className={classes.headerLabel}>Pending Action</div>
          {pendingActions.length === 1 ? (
            <div className={classes.pendingSingle}>{pendingActions[0]}</div>
          ) : pendingActions.length ? (
            <ol className={classes.pendingList}>
              {pendingActions.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ol>
          ) : (
            <div className={classes.headerValue}>None</div>
          )}
        </div>
        <div className={classes.headerField}>
          <div className={classes.headerLabel}>Assigned To</div>
          <div className={classes.headerValue}>{assignedTo || '-'}</div>
        </div>
        <div className={classes.headerField}>
          <div className={classes.headerLabel}>Batch Resets</div>
          {resetNumber > 0 ? (
            <span
              className={classes.resetLink}
              onClick={() => setResetsOpen(true)}
              role="button"
              tabIndex={0}
              onKeyPress={() => setResetsOpen(true)}
            >
              {resetNumber}
            </span>
          ) : (
            <span className={classes.resetPlain}>0</span>
          )}
        </div>
        {headerActions && <div className={classes.headerActions}>{headerActions}</div>}
      </div>

      {timeline}

      <Dialog
        open={resetsOpen}
        onClose={() => setResetsOpen(false)}
        classes={{ paper: classes.modalPaper }}
        maxWidth="md"
      >
        <div className={classes.modalHead}>
          <span
            className={classes.modalCancel}
            onClick={() => setResetsOpen(false)}
            role="button"
            tabIndex={0}
            onKeyPress={() => setResetsOpen(false)}
          >
            Cancel
            <IconButton size="small" aria-label="close" style={{ color: '#E02424' }}>
              <CloseIcon />
            </IconButton>
          </span>
        </div>
        <div className={classes.modalTitle}>
          Batch Reset{batchNumber ? ` for Batch No. ${batchNumber}` : ''}
        </div>
        <table className={classes.resetTable}>
          <thead>
            <tr>
              <th className={classes.resetTh}>Batch Reset Number</th>
              <th className={classes.resetTh}>Batch Reset by</th>
              <th className={classes.resetTh}>Reset Reason</th>
              <th className={classes.resetTh}>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {resetRows.map((r, i) => (
              <tr key={i}>
                <td className={classes.resetTd}>Reset {i + 1}</td>
                <td className={classes.resetTd}>{r.resetBy || '-'}</td>
                <td className={classes.resetTd}>{r.resetFor || '-'}</td>
                <td className={classes.resetTd}>
                  {r.timestamp && moment.utc(r.timestamp).isValid()
                    ? moment
                        .utc(r.timestamp)
                        .local()
                        .format('DD-MMM YYYY, HH:mm:ss')
                        .toUpperCase()
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Dialog>
    </div>
  );
}

export default withStyles(styles)(BatchProgressBar);
